import React, { useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useT } from "@/i18n/I18nContext";
import { GUIDE_CONTENT } from "@/lib/guideContent";
import {
  BookOpen,
  Compass,
  Milk,
  FlaskConical,
  Package,
  ScanSearch,
  UserCog,
  Settings,
} from "lucide-react";

// Map the string icon names used in guideContent.js to lucide components.
const ICONS = { Compass, Milk, FlaskConical, Package, ScanSearch, UserCog, Settings };

const scrollToSection = (id) => {
  const el = document.getElementById(`guide-${id}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Guide() {
  const { t, lang } = useT();
  const sections = useMemo(() => GUIDE_CONTENT[lang] || GUIDE_CONTENT.en, [lang]);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl space-y-6" data-testid="guide-page">
        {/* Intro banner */}
        <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-[hsl(var(--brand)/0.12)] to-[hsl(var(--brand)/0.02)] p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand))] text-white shadow-sm">
              <BookOpen className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="guide-title">{t("guide.title")}</h1>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("guide.subtitle")}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Table of contents */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-4">
              <p className="mb-2 px-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {t("guide.contents")}
              </p>
              <nav className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:gap-1 lg:overflow-visible">
                {sections.map((s) => {
                  const Icon = ICONS[s.icon] || Compass;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => scrollToSection(s.id)}
                      data-testid={`guide-toc-${s.id}`}
                      className="group flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-secondary hover:text-foreground lg:w-full"
                    >
                      <Icon className="h-[18px] w-[18px] text-[hsl(var(--brand))]" />
                      <span className="whitespace-nowrap lg:whitespace-normal">{s.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="space-y-6 lg:col-span-9">
            {sections.map((s) => {
              const Icon = ICONS[s.icon] || Compass;
              return (
                <Card key={s.id} id={`guide-${s.id}`} className="scroll-mt-4" data-testid={`guide-section-${s.id}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2.5 text-lg">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]">
                        <Icon className="h-[18px] w-[18px]" />
                      </span>
                      {s.title}
                    </CardTitle>
                    {s.blurb && <p className="pt-1 text-sm text-muted-foreground">{s.blurb}</p>}
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      {s.topics.map((topic, i) => (
                        <AccordionItem key={i} value={`${s.id}-${i}`} data-testid={`guide-topic-${s.id}-${i}`}>
                          <AccordionTrigger className="text-left text-sm font-semibold hover:no-underline">
                            {topic.q}
                          </AccordionTrigger>
                          <AccordionContent>
                            {Array.isArray(topic.a) ? (
                              <ul className="ml-1 list-inside list-disc space-y-1.5 text-sm text-muted-foreground">
                                {topic.a.map((line, j) => (
                                  <li key={j}>{line}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm leading-relaxed text-muted-foreground">{topic.a}</p>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
